import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { api, ApiError, tokenStore } from '@/lib/api';
import { connectionsApi, type OrganizationConnectionSummary } from '@/lib/connections';
import type { AuthResult, Organization, UserProfile } from '@/lib/types';

export interface SessionState {
  /** `unknown` until we have asked the server who this token belongs to. */
  status: 'unknown' | 'signed-out' | 'signed-in';
  user: UserProfile | null;
  organizations: Organization[];
  organizationId: string | null;
  loadingOrganizations: boolean;
  error: string | null;

  /**
   * Which calendar each organization has connected, keyed by organization id.
   *
   * A SEPARATE READ FROM THE ORGANIZATION LIST, because they come from
   * different services: the list is ZenCore's, which knows nothing about
   * calendar connections. An organization with nothing connected has no entry
   * here at all.
   */
  calendarByOrganization: Record<string, OrganizationConnectionSummary['connections']>;
  /**
   * Whether that read has happened. `idle`/`loading` must NOT render as "no
   * calendar connected" — saying so before we know is the bug this whole
   * summary exists to fix, and `failed` is not the same answer as "none"
   * either.
   */
  calendarStatus: 'idle' | 'loading' | 'ready' | 'failed';
}

const initialState: SessionState = {
  status: tokenStore.token() ? 'unknown' : 'signed-out',
  user: null,
  organizations: [],
  organizationId: tokenStore.organizationId(),
  loadingOrganizations: false,
  error: null,
  calendarByOrganization: {},
  calendarStatus: 'idle',
};

/**
 * Confirm the stored token still works and find out who it belongs to.
 *
 * Runs once on load. A token that has expired while the tab was closed resolves
 * to signed-out here rather than at the first click on a real screen.
 */
export const restoreSession = createAsyncThunk<
  { user: UserProfile; organizations: Organization[] },
  void,
  { rejectValue: string }
>('session/restore', async (_arg, { rejectWithValue }) => {
  try {
    const [user, organizations] = await Promise.all([
      api<UserProfile>('/auth/me', { withoutOrganization: true }),
      api<Organization[]>('/auth/organizations?limit=100', { withoutOrganization: true }),
    ]);
    return { user, organizations };
  } catch (err) {
    if (err instanceof ApiError && err.isAuthFailure) {
      return rejectWithValue('signed-out');
    }
    return rejectWithValue(
      err instanceof ApiError
        ? err.message
        : 'We could not reach ZenSpace to check your sign-in. Please try again.',
    );
  }
});

export const loadOrganizations = createAsyncThunk<Organization[], void, { rejectValue: string }>(
  'session/loadOrganizations',
  async (_arg, { rejectWithValue }) => {
    try {
      return await api<Organization[]>('/auth/organizations?limit=100', {
        withoutOrganization: true,
      });
    } catch (err) {
      return rejectWithValue(
        err instanceof ApiError ? err.message : 'We could not load your organizations.',
      );
    }
  },
);

/**
 * The calendar connected to each organization, for the picker's chips.
 *
 * Its own thunk rather than part of `loadOrganizations`: a failure here must
 * leave the picker perfectly usable — not knowing which calendar an
 * organization uses is no reason to be unable to choose one.
 */
export const loadCalendarSummaries = createAsyncThunk<OrganizationConnectionSummary[], void>(
  'session/loadCalendarSummaries',
  async () => connectionsApi.summary(),
);

/** Ends the session at ZenSpace, then locally whatever the outcome upstream. */
export const signOut = createAsyncThunk<void, void>('session/signOut', async () => {
  try {
    await api<unknown>('/auth/logout', { method: 'POST', withoutOrganization: true });
  } catch {
    // A failed logout upstream must not trap someone in a session they are
    // trying to leave. The local token goes either way.
  } finally {
    tokenStore.clear();
  }
});

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    signedIn(state, action: PayloadAction<AuthResult>) {
      tokenStore.setSession(action.payload.access_token, action.payload.refresh_token ?? null);
      state.status = 'signed-in';
      state.user = action.payload.user;
      state.error = null;
    },
    organizationChosen(state, action: PayloadAction<string>) {
      tokenStore.setOrganization(action.payload);
      state.organizationId = action.payload;
    },
    organizationCleared(state) {
      tokenStore.setOrganization(null);
      state.organizationId = null;
    },
    errorDismissed(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(restoreSession.pending, (state) => {
        state.loadingOrganizations = true;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.status = 'signed-in';
        state.user = action.payload.user;
        state.organizations = action.payload.organizations;
        state.loadingOrganizations = false;
        state.error = null;

        // A stored selection that is no longer in the list — someone removed
        // from that organization — must not linger, or every request 403s with
        // no way for them to see why.
        if (
          state.organizationId &&
          !action.payload.organizations.some((o) => o.id === state.organizationId)
        ) {
          tokenStore.setOrganization(null);
          state.organizationId = null;
        }

        // Skip the picker when there is nothing to pick.
        if (!state.organizationId && action.payload.organizations.length === 1) {
          const only = action.payload.organizations[0].id;
          tokenStore.setOrganization(only);
          state.organizationId = only;
        }
      })
      .addCase(restoreSession.rejected, (state, action) => {
        state.loadingOrganizations = false;
        if (action.payload === 'signed-out') {
          tokenStore.clear();
          state.status = 'signed-out';
          state.user = null;
          state.organizations = [];
          state.organizationId = null;
          state.error = null;
        } else {
          // Reaching ZenSpace failed, which is not the same as being signed
          // out. Say so, and keep the token.
          state.status = 'signed-out';
          state.error = action.payload ?? null;
        }
      })
      .addCase(loadOrganizations.pending, (state) => {
        state.loadingOrganizations = true;
      })
      .addCase(loadOrganizations.fulfilled, (state, action) => {
        state.organizations = action.payload;
        state.loadingOrganizations = false;
      })
      .addCase(loadOrganizations.rejected, (state, action) => {
        state.loadingOrganizations = false;
        state.error = action.payload ?? null;
      })
      .addCase(loadCalendarSummaries.pending, (state) => {
        state.calendarStatus = 'loading';
      })
      .addCase(loadCalendarSummaries.fulfilled, (state, action) => {
        state.calendarByOrganization = Object.fromEntries(
          action.payload.map((row) => [row.organization_id, row.connections]),
        );
        state.calendarStatus = 'ready';
      })
      .addCase(loadCalendarSummaries.rejected, (state) => {
        // Not an error the picker reports: it is one line on a card, and the
        // rest of the screen works without it.
        state.calendarStatus = 'failed';
      })
      .addCase(signOut.fulfilled, (state) => {
        state.status = 'signed-out';
        state.user = null;
        state.organizations = [];
        state.organizationId = null;
        state.error = null;
        state.calendarByOrganization = {};
        state.calendarStatus = 'idle';
      });
  },
});

export const { signedIn, organizationChosen, organizationCleared, errorDismissed } =
  sessionSlice.actions;

export default sessionSlice.reducer;
